/* eslint-disable react/no-unescaped-entities */
import PremiumHeader from "@/components/premium/PremiumHeader";
import SEO from "@/components/SEO";
import PremiumFooter from "@/components/premium/PremiumFooter";
import StickyContactBar from "@/components/premium/StickyContactBar";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Building2, Stethoscope, GraduationCap, Plane, Waves, Utensils, ShoppingBag, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const LocationPage = () => {
  const employers = [
    { name: "L3Harris Technologies", distance: "15 min", type: "Aerospace & Defense" },
    { name: "Northrop Grumman", distance: "20 min", type: "Aerospace" },
    { name: "Collins Aerospace", distance: "25 min", type: "Aerospace" },
    { name: "Health First", distance: "10 min", type: "Healthcare System" },
    { name: "Florida Tech", distance: "12 min", type: "University" },
    { name: "Melbourne CBD", distance: "10 min", type: "Business District" },
  ];

  const lifestyle = [
    { icon: Waves, name: "Atlantic Beach", distance: "3 min walk" },
    { icon: Utensils, name: "Restaurants", distance: "5-10 min" },
    { icon: ShoppingBag, name: "Shopping", distance: "10 min" },
    { icon: Plane, name: "MLB Airport", distance: "20 min" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Location | 3-Minute Walk to the Beach — Indialantic, FL 32903 | Space Coast"
        description="Paradise Beach Rental sits in Indialantic on Florida's Space Coast — a 3-minute walk to the beach and minutes from Melbourne, L3Harris, hospitals, dining and shopping."
        path="/location"
      />
      <PremiumHeader />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-[#0A1628]">
          <div className="container-premium text-center">
            <div className="gold-line mx-auto mb-6" />
            <span className="label-sm text-[#C9A962]">Location</span>
            <h1 className="headline-xl text-white mt-4 mb-6">Ideally Positioned</h1>
            <p className="body-lg text-white/70 max-w-2xl mx-auto">
              Minutes from major employers, healthcare, and the beach. The perfect base for your extended stay.
            </p>
          </div>
        </section>

        {/* Map Section */}
        <section className="section-lg">
          <div className="container-premium">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-lg mb-4">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3520.9!2d-80.5667!3d28.0883!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88de11f6d1d1d1d1%3A0x1234567890abcdef!2s2161%20Todd%20Ln%2C%20Indialantic%2C%20FL%2032903!5e0!3m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    title="Location"
                  />
                </div>
                <div className="flex items-center gap-2 text-[#0A1628]/60">
                  <MapPin className="w-4 h-4" />
                  <span className="font-body text-sm">2161 Todd Ln, Indialantic, FL 32903</span>
                </div>
              </div>

              <div>
                <h2 className="headline-md text-[#0A1628] mb-6">Major Employers Nearby</h2>
                <div className="space-y-4 mb-10">
                  {employers.map((emp, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center justify-between py-3 border-b border-[#0A1628]/10"
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-[#C9A962]" />
                        <div>
                          <p className="font-body font-medium text-[#0A1628] text-sm">{emp.name}</p>
                          <p className="font-body text-xs text-[#0A1628]/50">{emp.type}</p>
                        </div>
                      </div>
                      <span className="font-body text-sm text-[#0A1628]/60">{emp.distance}</span>
                    </motion.div>
                  ))}
                </div>

                <h3 className="font-body font-semibold text-[#0A1628] mb-4">Lifestyle & Amenities</h3>
                <div className="grid grid-cols-2 gap-4">
                  {lifestyle.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-[#F7F3ED] p-4">
                      <item.icon className="w-5 h-5 text-[#C9A962]" />
                      <div>
                        <p className="font-body text-sm font-medium text-[#0A1628]">{item.name}</p>
                        <p className="font-body text-xs text-[#0A1628]/50">{item.distance}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Long-form location content — SEO ranking signal */}
        <section className="section-lg bg-[#F7F3ED]">
          <div className="container-premium max-w-4xl">
            <div className="gold-line mb-6" />
            <span className="label-sm text-[#C9A962]">Complete Location Guide</span>
            <h2 className="headline-lg text-[#0A1628] mt-3 mb-10">
              Living in Indialantic: A Complete Guide for Extended-Stay Guests
            </h2>
            <div className="space-y-6 text-[#0A1628]/75 text-lg leading-relaxed">
              <p>
                Indialantic is a small barrier-island town of about 3,000 residents on Florida's
                Space Coast, tucked between the Atlantic Ocean and the Indian River Lagoon. If
                you've been researching a monthly rental in Melbourne, Melbourne Beach,
                Satellite Beach or anywhere in Brevard County, Indialantic is worth a serious
                look — it's a rare combination of residential quiet, walkable beach access, and
                genuinely fast commutes to the Space Coast's biggest employers.
              </p>
              <p>
                Our home sits at 2161 Todd Ln, three minutes on foot from the Atlantic and about
                a mile from downtown Indialantic. That means you can walk to the beach in the
                morning, bike to the Indialantic Boardwalk for coffee, drive to L3Harris in 15
                minutes for work, and be back on the sand by five — all without ever getting on
                I-95. Very few Florida beach rentals actually deliver that kind of geography.
              </p>

              <h3 className="font-display text-2xl text-[#0A1628] pt-6">Why Extended-Stay Guests Choose Indialantic</h3>
              <p>
                The three most common extended-stay guests in Brevard County are traveling
                nurses on 13-week contracts, aerospace/defense contractors on 3-to-9-month
                rotations, and snowbirds escaping northern winters for 3-to-6 months. Indialantic
                happens to be nearly perfectly positioned for all three:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Health First Holmes Regional Medical Center</strong> is a 10-minute drive across the causeway — one of the shortest hospital commutes on the barrier island.</li>
                <li><strong>L3Harris Technologies</strong> (Brevard's largest employer) is 15 minutes away in Palm Bay.</li>
                <li><strong>Northrop Grumman</strong> Melbourne campus is 20 minutes; <strong>Collins Aerospace</strong> is 25.</li>
                <li><strong>Patrick Space Force Base</strong> is 15 minutes north up A1A.</li>
                <li><strong>Melbourne International Airport (MLB)</strong> is 20 minutes — with non-stop flights to Atlanta, Charlotte, DFW and more.</li>
                <li><strong>Kennedy Space Center</strong> is 45 minutes north — close enough for launch viewing right from the front yard.</li>
              </ul>

              <h3 className="font-display text-2xl text-[#0A1628] pt-6">Indialantic vs. Melbourne Beach vs. Cocoa Beach</h3>
              <p>
                All three are Space Coast beach towns and they're often searched interchangeably.
                Here's the honest breakdown for a 90-day+ stay:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Indialantic</strong> (where we are) — Residential, quiet, walkable, fastest access to hospitals and downtown Melbourne. Best for guests who want a real neighborhood feel plus close-in commutes.</li>
                <li><strong>Melbourne Beach</strong> — Even quieter, more spread out, further south. Beautiful but longer drives to essentials. Great if you're not commuting much.</li>
                <li><strong>Cocoa Beach</strong> — More commercial, more tourist-oriented, closer to Kennedy Space Center but 40+ minutes from Melbourne hospitals and downtown. Best for KSC-focused stays.</li>
                <li><strong>Satellite Beach / Indian Harbour Beach</strong> — Between Indialantic and Cocoa Beach. Similar residential feel to Indialantic, closest to Patrick Space Force Base.</li>
              </ul>

              <h3 className="font-display text-2xl text-[#0A1628] pt-6">The Actual Beach — What to Expect</h3>
              <p>
                Indialantic Beach is a wide, mostly-uncrowded stretch of Atlantic coast. It's
                lifeguarded seasonally at the main Fifth Avenue access point (a 3-minute walk
                from the front door) and open 24 hours. Water temperature averages 71°F in
                January and 84°F in August, which makes swimming legitimately possible
                year-round — a rarity even in Florida.
              </p>
              <p>
                Between May and October, the beach is an active loggerhead and green sea turtle
                nesting site. You'll see roped-off nests all along the sand — guests routinely
                witness hatchlings emerge if their stay overlaps with August-October. The
                Sea Turtle Preservation Society runs guided walks that book up months in advance.
              </p>

              <h3 className="font-display text-2xl text-[#0A1628] pt-6">Food, Coffee & Everyday Life</h3>
              <p>
                Downtown Indialantic is a five-minute walk from the house. Long Doggers is the
                de-facto local dive; Djon's Steak & Lobster is the special-occasion pick;
                Cafe Coconut Cove and Off the Traxx handle the morning coffee routine. For
                groceries, Publix is a five-minute drive across the causeway, with Target,
                Costco, and Whole Foods reachable in 10-15 minutes.
              </p>
              <p>
                Cross the Melbourne Causeway and you're in downtown Melbourne's New Haven
                Avenue restaurant strip — a legitimately good after-work scene with dozens
                of restaurants, breweries and live music venues. It's the closest thing the
                Space Coast has to a "real downtown" and it's ten minutes from our front door.
              </p>

              <h3 className="font-display text-2xl text-[#0A1628] pt-6">The Bottom Line</h3>
              <p>
                If you're extending a stay on Florida's Space Coast — for work, for a season,
                for a hospital contract, or for a relocation runway — Indialantic delivers
                what most Florida beach rentals only claim: a real house in a real neighborhood
                with a real 3-minute walk to the ocean and a real 15-minute commute to real
                employers. Contact us directly and we'll get you a rate for your dates within
                the hour.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#0A1628]">
          <div className="container-premium text-center">
            <h3 className="headline-md text-white mb-4">Ready to Make This Your Home Base?</h3>
            <p className="body-md text-white/60 mb-8">Contact us to check availability for your dates.</p>
            <Link to="/contact">
              <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-4">
                Request Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <PremiumFooter />
      <StickyContactBar />
    </div>
  );
};

export default LocationPage;
